import { useContext, useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DataContext from "../../../context/DataContext";
import { DocumentsTopNavbar } from "../../../components/documents/DocumentsTopNavbar";
import { COILineItemForm } from "../../../components/documents/coi/COILineItemForm";
import { COIResolveDialog } from "../../../components/documents/coi/COIResolveDialog";
import { COIStatusBadge } from "../../../components/documents/coi/COIStatusBadge";
import { COIUploadForm } from "../../../components/documents/coi/COIUploadForm";
import {
  calculateCOINewRuntime,
  formatCOIDate,
  formatCOIInteger,
} from "../../../components/documents/coi/coiFormatters";
import { DeleteEntryDialog } from "../../../components/documents/pump/DeleteEntryDialog";
import { PumpStatusBadge } from "../../../components/documents/pump/PumpStatusBadge";
import { ReportsAdmin } from "../reports/ReportsAdmin";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";
import { useFetch } from "../../../hooks/useFetch";
import { usePaginator } from "../../../hooks/usePaginator";
import { usePermissions } from "../../../hooks/usePermissions";
import { useRequest } from "../../../hooks/useRequest";
import { useToolsRunTrackedWells } from "../../../hooks/useToolsRunTrackedWells";
import {
  ClientProps,
  COILineItemProps,
  COIRecordType,
  PumpEntryStatus,
} from "../../../interfaces/interfaces";

const EXCLUDE_ERROR = "__exclude_error__";
// Distinct, non-empty sentinel for the explicit "All statuses (incl. errors)"
// option — ListPageLayout's own auto-injected default option already owns
// value="" for its "All" entry (which maps to EXCLUDE_ERROR below), so the
// true "no status filter at all" state needs its own reachable value here.
const ALL_STATUSES = "__all_statuses__";

interface COIResponse {
  success?: boolean;
  data: COILineItemProps[];
}

interface Props {
  clientMode?: boolean;
  assignedClientOptions?: ClientProps[];
  assignedClientId?: number | null;
  onAssignedClientChange?: (clientId: number | null) => void;
}

const reasonLabel = (reason?: string | null) =>
  reason?.replaceAll("_", " ") || "";

export const COIAdmin = ({
  clientMode = false,
  assignedClientOptions = [],
  assignedClientId = null,
  onAssignedClientChange,
}: Props) => {
  const { paginationModel, setPaginationModel, resetPage } =
    usePaginator("coi-inventory");
  const {
    clientOptions: globalClientOptions,
    fetchDataClient,
    fetchDataProducts,
  } = useContext(DataContext);
  const { handleRequest } = useRequest();
  const { wellOptions, fetchDataWells } = useToolsRunTrackedWells();
  const { canManageCOI } = usePermissions();
  const clientOptions = clientMode
    ? assignedClientOptions
    : globalClientOptions;
  const [recordType, setRecordType] = useState<COIRecordType>("inventory");
  const [showReports, setShowReports] = useState(false);
  const [clientId, setClientId] = useState("");
  const [wellId, setWellId] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [status, setStatus] = useState(EXCLUDE_ERROR);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resolveRow, setResolveRow] = useState<COILineItemProps | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<COILineItemProps | null>(null);
  const [deletingRow, setDeletingRow] = useState<COILineItemProps | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!clientMode) {
      fetchDataClient();
      fetchDataProducts();
    }
    // Master data is intentionally loaded once per page mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientMode]);

  useEffect(() => {
    if (!clientMode || !assignedClientId) return;
    setClientId(String(assignedClientId));
    setWellId("");
    fetchDataWells(assignedClientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedClientId, clientMode]);

  useEffect(() => {
    if (!clientId) {
      setCategories([]);
      return;
    }
    let active = true;
    handleRequest({
      endpoint: `coi-categories/client/${clientId}`,
      options: { method: "GET" },
    })
      .then((response) => {
        if (active) setCategories(response.data || []);
      })
      .catch(() => {
        if (active) setCategories([]);
      });
    return () => {
      active = false;
    };
    // Request handler is stable enough for client-id scoped loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const fetchFilters = useMemo(
    () => ({
      recordType,
      clientId,
      wellId,
      search,
      category,
      status: status === EXCLUDE_ERROR ? "" : status,
      excludeStatus: status === EXCLUDE_ERROR ? "error" : "",
      dateFrom,
      dateTo,
    }),
    [
      recordType,
      clientId,
      wellId,
      search,
      category,
      status,
      dateFrom,
      dateTo,
    ],
  );
  const { data, isLoading, error, fetchData, pagination } =
    useFetch<COIResponse>(
      showReports ? undefined : "coi-paginate",
      paginationModel,
      undefined,
      undefined,
      fetchFilters,
    );
  const rows = data?.data || [];
  const chooseClient = (value: string) => {
    const nextClientId =
      clientMode && !value && assignedClientId
        ? String(assignedClientId)
        : value;
    setClientId(nextClientId);
    setWellId("");
    setCategory("");
    if (clientMode) {
      onAssignedClientChange?.(
        nextClientId ? Number(nextClientId) : null,
      );
    }
    if (nextClientId) fetchDataWells(Number(nextClientId));
    // Client lives in toolbarActions, not the `filters` array, so
    // ListPageLayout's own filterSignature effect won't reset the page for
    // it — reset explicitly here, same as ToolsRunAdmin/ReportsAdmin.
    resetPage();
  };
  const clearFilters = () => {
    setWellId("");
    setSearch("");
    setCategory("");
    setStatus(EXCLUDE_ERROR);
    setDateFrom("");
    setDateTo("");
    resetPage();
  };

  const updateRowBusinessStatus = async (row: COILineItemProps, nextStatus: PumpEntryStatus | "") => {
    await handleRequest({
      endpoint: `coi/${row.id}`,
      options: { method: "PUT", body: JSON.stringify({ businessStatus: nextStatus || null }) },
    });
    fetchData();
  };

  const confirmDeleteRow = async () => {
    if (!deletingRow) return;
    setDeleting(true);
    try {
      await handleRequest({
        endpoint: `coi/${deletingRow.id}`,
        options: { method: "DELETE" },
      });
      setDeletingRow(null);
      fetchData();
    } finally {
      setDeleting(false);
    }
  };

  const columns: ListColumn<COILineItemProps>[] =
    recordType === "inventory"
      ? [
          {
            key: "part",
            header: "P/N / Description",
            render: (r) => (
              <>
                <Typography variant="body2" fontWeight={850}>
                  {r.partNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.product?.name || r.equipmentDescription || "—"}
                </Typography>
              </>
            ),
            width: "1.6fr",
          },
          {
            key: "category",
            header: "Category",
            render: (r) => r.equipmentCategory || "—",
          },
          {
            key: "quantity",
            header: "Qty",
            render: (r) => formatCOIInteger(r.quantity),
            sortAccessor: (r) => r.quantity,
          },
          {
            key: "installDate",
            header: "Install date",
            render: (r) => formatCOIDate(r.installDate),
            sortAccessor: (r) => r.installDate,
          },
          {
            key: "pullDate",
            header: "Pull date",
            render: (r) => formatCOIDate(r.pullDate),
            sortAccessor: (r) => r.pullDate,
          },
          {
            key: "runtime",
            header: "Runtime",
            render: (r) => formatCOIInteger(r.runtimeDays),
          },
          {
            key: "client",
            header: "Client / From",
            render: (r) => (
              <>
                <Typography variant="body2" fontWeight={750}>
                  {r.client?.name || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.well?.name || r.fromWellRaw || "—"}
                </Typography>
              </>
            ),
          },
          {
            key: "to",
            header: "To",
            render: (r) => r.toText || "—",
          },
        ]
      : [
          {
            key: "part",
            header: "P/N / Description",
            render: (r) => (
              <>
                <Typography variant="body2" fontWeight={850}>
                  {r.partNumber}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.product?.name || r.equipmentDescription || "—"}
                </Typography>
              </>
            ),
            width: "1.6fr",
          },
          {
            key: "category",
            header: "Category",
            render: (r) => r.equipmentCategory || "—",
          },
          {
            key: "quantity",
            header: "Qty",
            render: (r) => formatCOIInteger(r.quantity),
            sortAccessor: (r) => r.quantity,
          },
          {
            key: "installDate",
            header: "Install date",
            render: (r) => formatCOIDate(r.installDate),
            sortAccessor: (r) => r.installDate,
          },
          {
            key: "pullDate",
            header: "Pull date",
            render: (r) => formatCOIDate(r.pullDate),
            sortAccessor: (r) => r.pullDate,
          },
          {
            key: "client",
            header: "Client / From",
            render: (r) => (
              <>
                <Typography variant="body2" fontWeight={750}>
                  {r.client?.name || "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.well?.name || r.fromWellRaw || "—"}
                </Typography>
              </>
            ),
          },
          {
            key: "to",
            header: "To",
            render: (r) => r.toText || "—",
          },
          {
            key: "reinstallDate",
            header: "Reinstall date",
            render: (r) => formatCOIDate(r.reinstallDate),
            sortAccessor: (r) => r.reinstallDate,
          },
          {
            key: "newRuntime",
            header: "New runtime",
            render: (r) =>
              `${formatCOIInteger(calculateCOINewRuntime(r.reinstallDate))} days`,
          },
        ];

  // Status is only shown to internal users, matching PumpModuleAdmin/ToolsRunAdmin
  // which hide their own Status filter from clientMode users the same way. Well
  // and Category are genuine narrowing filters (not the persistent Client scope),
  // so they stay in `filters` as autocomplete/select-kind ListFilterFields.
  const filters: ListFilterField[] = [
    {
      kind: "autocomplete",
      key: "wellId",
      label: "From well",
      value: wellId,
      onChange: setWellId,
      options: wellOptions.map((well) => ({
        value: String(well.id),
        label: well.name,
      })),
      placeholder: clientId ? "Search wells…" : "Select a client",
      disabled: !clientId,
    },
    {
      kind: "select",
      key: "category",
      label: "Category",
      value: category,
      onChange: setCategory,
      options: categories.map((option) => ({ value: option, label: option })),
    },
    ...(!clientMode
      ? [
          {
            kind: "select" as const,
            key: "status",
            label: "Status",
            // ListPageLayout's own auto-injected default option is value="" —
            // that's the original default behavior (matched + needs_review,
            // errors excluded), so EXCLUDE_ERROR maps to it. The literal ""
            // that used to mean "all statuses including errors" now has its
            // own reachable option below (ALL_STATUSES) instead of being
            // silently unreachable.
            value:
              status === EXCLUDE_ERROR
                ? ""
                : status === ""
                  ? ALL_STATUSES
                  : status,
            onChange: (value: string) =>
              setStatus(
                value === "" ? EXCLUDE_ERROR : value === ALL_STATUSES ? "" : value,
              ),
            options: [
              { value: ALL_STATUSES, label: "All statuses (incl. errors)" },
              { value: "matched", label: "Matched" },
              { value: "needs_review", label: "Needs review" },
              { value: "error", label: "Error" },
            ],
          },
        ]
      : []),
    {
      kind: "date",
      key: "dateFrom",
      label: recordType === "inventory" ? "Pull from" : "Reinstall from",
      value: dateFrom,
      onChange: setDateFrom,
    },
    {
      kind: "date",
      key: "dateTo",
      label: "To date",
      value: dateTo,
      onChange: setDateTo,
    },
  ];

  const inventoryList = (
    <ListPageLayout<COILineItemProps>
      title={recordType === "inventory" ? "Inventory" : "Reinstalled"}
      columns={columns}
      rows={rows}
      getRowId={(r) => r.id}
      rowsLoading={isLoading}
      isError={Boolean(error)}
      errorMessage="Customer Owned Inventory could not be loaded."
      emptyTitle={`No ${recordType === "inventory" ? "inventory" : "reinstalled"} rows found`}
      emptySubtitle={
        clientMode
          ? "Adjust the filters to find customer-owned tools."
          : "Select a client, adjust filters, or import a workbook."
      }
      searchValue={search}
      onSearchChange={(value) => setSearch(value.trim())}
      searchPlaceholder="P/N, description or To…"
      filters={filters}
      page={paginationModel.page}
      pageSize={paginationModel.pageSize}
      totalCount={Number(pagination.totalRecords || 0)}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      toolbarActions={
        <Box sx={{ width: { xs: "100%", sm: 220 } }}>
          <Box
            component="label"
            sx={{
              display: "block",
              mb: "5px",
              color: "#9CA1AB",
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: ".6px",
              lineHeight: 1.2,
              textTransform: "uppercase",
            }}
          >
            Client
          </Box>
          <Autocomplete
            size="small"
            options={clientOptions}
            value={
              clientOptions.find(
                (client) => String(client.id) === clientId,
              ) || null
            }
            getOptionLabel={(client) => client.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_, client) =>
              chooseClient(client ? String(client.id) : "")
            }
            disableClearable={clientMode && clientOptions.length === 1}
            disabled={clientMode && clientOptions.length === 1}
            autoHighlight
            openOnFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                minHeight: 38,
                bgcolor: "#FAFAF8",
                borderRadius: "9px",
                fontSize: 13,
                "& fieldset": { borderColor: "#E2E2DD" },
                "&:hover fieldset": { borderColor: "#C9C7BE" },
                "&.Mui-focused fieldset": {
                  borderColor: "#ED8B00",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": { py: "8px" },
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search clients…" />
            )}
          />
        </Box>
      }
      rowStatus={(r) => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <COIStatusBadge status={r.status} />
            {canManageCOI ? (
              <FormControl size="small" sx={{ minWidth: 108 }}>
                <Select
                  value={r.businessStatus || ""}
                  displayEmpty
                  onChange={(e) =>
                    updateRowBusinessStatus(r, e.target.value as PumpEntryStatus | "")
                  }
                  sx={{ fontSize: 12 }}
                >
                  <MenuItem value="">Not set</MenuItem>
                  <MenuItem value="in-stock">In stock</MenuItem>
                  <MenuItem value="reused">Reused</MenuItem>
                </Select>
              </FormControl>
            ) : r.businessStatus ? (
              <PumpStatusBadge status={r.businessStatus} />
            ) : null}
          </Box>
          {r.status !== "matched" ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "capitalize" }}
            >
              {reasonLabel(r.errorReason || r.reviewReason)}
            </Typography>
          ) : null}
        </Box>
      )}
      rowActions={(r) => (
        <>
          {canManageCOI && r.status === "needs_review" ? (
            <Button
              size="small"
              startIcon={<AutoFixHighRoundedIcon />}
              onClick={() => setResolveRow(r)}
              sx={{ color: "#A65F00", fontWeight: 800 }}
            >
              Resolve
            </Button>
          ) : null}
          {canManageCOI ? (
            <>
              <IconButton
                size="small"
                onClick={() => {
                  setEditingRow(r);
                  setFormOpen(true);
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setDeletingRow(r)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </>
          ) : null}
        </>
      )}
    />
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F3F3F0",
        color: "#29313D",
      }}
    >
      <DocumentsTopNavbar
        title="Customer Owned Inventory"
        actions={
          !showReports && canManageCOI ? (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<AddCircleOutlineRoundedIcon />}
                onClick={() => {
                  setEditingRow(null);
                  setFormOpen(true);
                }}
                sx={{
                  borderColor: "#E7E7E3",
                  color: "#2A2F38",
                  borderRadius: "9px",
                  px: "15px",
                  py: "8px",
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
                }}
              >
                Add Record
              </Button>
              <Button
                variant="outlined"
                startIcon={<UploadFileRoundedIcon />}
                onClick={() => setUploadOpen(true)}
                sx={{
                  borderColor: "#E7E7E3",
                  color: "#2A2F38",
                  borderRadius: "9px",
                  px: "15px",
                  py: "8px",
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": { borderColor: "#ED8B00", color: "#ED8B00" },
                }}
              >
                Import Excel
              </Button>
            </Box>
          ) : undefined
        }
      />
      <Box sx={{ maxWidth: 1680, mx: "auto", p: { xs: 2, md: 3.5 } }}>
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            border: "1px solid #E7E7E3",
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={showReports ? "reports" : recordType}
            onChange={(_, value: COIRecordType | "reports") => {
              if (value === "reports") {
                setShowReports(true);
                resetPage();
                return;
              }
              setShowReports(false);
              setRecordType(value);
              setDateFrom("");
              setDateTo("");
              resetPage();
            }}
            sx={{
              px: 2,
              minHeight: 52,
              borderBottom: "1px solid #ECEBE6",
              "& .MuiTab-root": { minHeight: 52, fontSize: 12, fontWeight: 850 },
              "& .MuiTabs-indicator": { bgcolor: "#ED8B00", height: 3 },
            }}
          >
            <Tab value="inventory" label="Inventory" />
            <Tab value="reinstall" label="Reinstall" />
            <Tab value="reports" label="Dashboard" />
          </Tabs>
        </Paper>

        {showReports ? (
          <ReportsAdmin
            embedded
            clientMode={clientMode}
            assignedClientOptions={assignedClientOptions}
            assignedClientId={assignedClientId}
            onAssignedClientChange={onAssignedClientChange}
          />
        ) : (
          inventoryList
        )}
      </Box>
      <COIUploadForm
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(result) => {
          const importedClientId = result.clientId;
          if (importedClientId) {
            setClientId(String(importedClientId));
            fetchDataWells(importedClientId);
          }
          setRecordType("inventory");
          clearFilters();
          fetchData();
          setResolveRow(
            result.rows.find((row) => row.status === "needs_review") || null,
          );
        }}
      />
      <COIResolveDialog
        row={resolveRow}
        onClose={() => setResolveRow(null)}
        onResolved={fetchData}
      />
      <COILineItemForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingRow(null);
        }}
        onSaved={fetchData}
        clientOptions={clientOptions}
        categories={categories}
        initialClientId={clientId ? Number(clientId) : null}
        lockedClientId={clientMode ? assignedClientId : null}
        editingRow={editingRow}
      />
      <DeleteEntryDialog
        open={Boolean(deletingRow)}
        onClose={() => setDeletingRow(null)}
        onConfirm={confirmDeleteRow}
        confirming={deleting}
        title="Delete inventory record"
        description="This will permanently remove this inventory record. This action cannot be undone."
      />
    </Box>
  );
};
